import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Globe, Repeat, Trash2 } from "lucide-react";
import { errMsg } from "../../lib/api";
import { Tracker } from "./domain/Tracker";
import { StartStep } from "./domain/StartStep";
import { Conflicts, RecordsTable } from "./domain/RecordsTable";
import { GuidePanel, Troubleshooting } from "./domain/GuidePanel";
import { StatusPanel } from "./domain/StatusPanel";
import { History } from "./domain/History";
import { ForwardGuide, ForwardStatus } from "./domain/ForwardGuide";

function Published({ d, fw }) {
  const ok = d.health !== "problem";
  if (fw) return (
    <div className={`rounded-xl p-4 text-sm ${ok ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`} data-testid="domain-published">
      <p className="font-semibold flex items-center gap-1.5 text-[#062a20]"><CheckCircle2 className="w-4 h-4 text-emerald-600" />{ok ? "Your domain is connected" : "Your domain needs attention"}</p>
      <p className="mt-1">{ok ? <>Visitors who open <a href={`http://${d.hostname}`} target="_blank" rel="noreferrer" className="underline font-semibold" data-testid="domain-live-link">{d.hostname}</a> reach your website.</> : "Your domain no longer forwards to your website. Check the forwarding at your provider."}</p>
      {ok && d.records?.some((r) => r.state === "ok" && !r.masked) && <p className="mt-2 text-xs text-amber-900 bg-amber-50 rounded p-2" data-testid="domain-mask-tip">The address bar currently changes to your Altiora link. To keep <b>{d.hostname}</b> visible, open your provider's forwarding settings and switch to <b>forwarding with masking</b> (GoDaddy: "Forward with masking"), then click Verify.</p>}
    </div>);
  return (
    <div className={`rounded-xl p-4 text-sm ${ok ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`} data-testid="domain-published">
      <p className="font-semibold flex items-center gap-1.5 text-[#062a20]"><CheckCircle2 className="w-4 h-4 text-emerald-600" />{ok ? "Your website is live on your domain" : "Your domain needs attention"}</p>
      <p className="mt-1">{ok ? <>Visit <a href={`https://${d.hostname}`} target="_blank" rel="noreferrer" className="underline font-semibold" data-testid="domain-live-link">https://{d.hostname}</a>. The secure padlock is active.</> : "Your website is still live on its Altiora link. Check your DNS records below."}</p>
    </div>);
}

export function DomainCard({ project, api, reload }) {
  const [st, setSt] = useState(null);
  const pid = project.id;
  const load = useCallback(() => api.get(`/projects/${pid}/domain`).then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e))), [api, pid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const d = st?.domain;
    if (!d || d.published || !["propagating", "partial"].includes(d.dns_status) && d.ssl_status !== "processing") return;
    const t = setInterval(() => api.get(`/projects/${pid}/domain?refresh=1`).then((r) => setSt(r.data)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [st, api, pid]);
  const act = async (method, path, body) => {
    try {
      const url = `/projects/${pid}/domain${path && !path.startsWith("?") ? "/" + path : path}`;
      const r = await (method === "get" ? api.get(url) : api[method](url, body));
      setSt(r.data);
      if (path === "publish") { toast.success("Your website is now live on your domain!"); reload(); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (replace) => {
    if (!window.confirm(replace ? "Remove this domain so you can add a different one?" : `Disconnect ${st.domain.hostname}? Your website stays live on its Altiora link.`)) return;
    try { await api.delete(`/projects/${pid}/domain`); toast.success("Domain removed"); await load(); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!st) return null;
  const d = st.domain;
  const mailto = `mailto:${st.support_email}?subject=${encodeURIComponent(`Domain help: ${d?.hostname || "new domain"}`)}&body=${encodeURIComponent(`Project: ${pid}\nDomain: ${d?.hostname || "-"}\nDNS status: ${d?.dns_status || "-"}\nSSL status: ${d?.ssl_status || "-"}\n\nWhat I need help with:\n`)}`;
  return (
    <div className="card-lux text-sm space-y-5 md:col-span-2" data-testid="domain-card">
      <div>
        <h2 className="text-2xl text-[#062a20] flex items-center gap-2"><Globe className="w-5 h-5" />Connect my purchased domain</h2>
        <p className="text-slate-600">Use a domain you already own, like example.com. We'll guide you step by step. No technical knowledge needed.</p>
      </div>
      <Tracker steps={st.tracker} />
      {!st.eligible.ok && !d && <p className="text-slate-600 bg-slate-50 rounded-lg p-3" data-testid="domain-not-eligible">{st.eligible.reason}</p>}
      {st.eligible.ok && !d && st.mode === "forward" && st.needs_publish && <p className="text-slate-700 bg-[#f4efe2] rounded-lg p-3" data-testid="domain-publish-first">First publish your website: open the <b>Live editor</b> tab and click <b>Publish</b>. Then come back here to connect your domain.</p>}
      {st.eligible.ok && !d && !(st.mode === "forward" && st.needs_publish) && <StartStep st={st} pid={pid} api={api} onAdded={setSt} />}
      {d && <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-base text-[#062a20]" data-testid="domain-hostname">{d.hostname}</b><span className="text-xs text-slate-500">via {d.provider_name}</span>
          <span className="flex-1" />
          <button onClick={() => remove(true)} className="chip" data-testid="domain-replace"><Repeat className="w-3.5 h-3.5" />Change domain</button>
          <button onClick={() => remove(false)} className="chip !text-red-700" data-testid="domain-remove"><Trash2 className="w-3.5 h-3.5" />Disconnect</button>
        </div>
        {d.published && <Published d={d} fw={st.mode === "forward"} />}
        {(!d.published || d.health === "problem") && (st.mode === "forward" ? <>
          {d.dns_status !== "verified" && <ForwardGuide d={d} forwardTo={st.forward_to} />}
          <ForwardStatus records={d.records} />
        </> : <>
          {d.dns_status !== "verified" && <GuidePanel d={d} />}
          <Conflicts conflicts={d.conflicts} />
          <RecordsTable records={d.records} />
        </>)}
        <StatusPanel d={d} st={st} act={act} providers={st.providers} mailto={mailto} />
        <Troubleshooting />
        <History api={api} pid={pid} stamp={d.last_check_at} />
      </div>}
    </div>);
}
